import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Plus,
  Pencil,
  Trash2,
  Users,
  Search,
  ArrowLeft,
  Loader2,
  UserPlus,
  X,
  Check,
  FileText,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';

const WAGroupsPage = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await api.get('/wa/groups');
      setGroups(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingGroup(null);
    setFormData({ name: '', description: '' });
    setShowCreateModal(true);
  };

  const openEditModal = (group) => {
    setEditingGroup(group);
    setFormData({ name: group.name, description: group.description || '' });
    setShowCreateModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingGroup) {
        await api.put(`/wa/groups/${editingGroup.id}`, formData);
      } else {
        await api.post('/wa/groups', formData);
      }
      setShowCreateModal(false);
      fetchGroups();
    } catch (error) {
      alert('Error saving group');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group? All members will be removed.')) return;
    
    try {
      await api.delete(`/wa/groups/${groupId}`);
      fetchGroups();
    } catch (error) {
      alert('Error deleting group');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/whatsapp')}
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Client Groups</h1>
            <p className="text-gray-500 mt-1">Organize your clients into groups for targeted broadcasts</p>
          </div>
        </div>
        <Button 
          onClick={openCreateModal}
          className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
        >
          <Plus size={16} className="mr-2" />
          Create Group
        </Button>
      </div>

      {/* Groups Table */}
      <Card className="bg-white border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Group Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Members</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {groups.length > 0 ? groups.map((group) => (
                <tr key={group.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#4F46E5]/10 flex items-center justify-center">
                        <Users className="text-[#4F46E5]" size={20} />
                      </div>
                      <p className="font-medium text-gray-900">{group.name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-gray-600">
                    {group.description || '-'}
                  </td>
                  <td className="px-4 py-4">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                      <Users size={14} />
                      {group.member_count}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-gray-500">
                    {new Date(group.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/whatsapp/groups/${group.id}`)}
                      >
                        View Members
                      </Button>
                      <button 
                        onClick={() => openEditModal(group)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Pencil size={16} className="text-gray-500" />
                      </button>
                      <button 
                        onClick={() => handleDelete(group.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="px-4 py-12 text-center">
                    <Users className="mx-auto text-gray-300 mb-4" size={48} />
                    <h3 className="font-semibold text-gray-900 mb-2">No groups yet</h3>
                    <p className="text-gray-500 mb-4">Create your first client group to get started</p>
                    <Button onClick={openCreateModal}>
                      <Plus size={16} className="mr-2" />
                      Create Group
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? 'Edit Group' : 'Create Group'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., VIP Customers"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Customers who spent more than RM1000"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              >
                {saving ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : null}
                {editingGroup ? 'Save Changes' : 'Create Group'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Group Detail Page Component
export const WAGroupDetailPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [adding, setAdding] = useState(false);
  
  // Bulk import state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkPhoneText, setBulkPhoneText] = useState('');
  const [bulkMatches, setBulkMatches] = useState([]);
  const [bulkNotFound, setBulkNotFound] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [includeUnmatched, setIncludeUnmatched] = useState(false);

  useEffect(() => {
    fetchGroupData();
  }, [groupId]);

  const fetchGroupData = async () => {
    try {
      const [groupRes, membersRes] = await Promise.all([
        api.get(`/wa/groups/${groupId}`),
        api.get(`/wa/groups/${groupId}/members`)
      ]);
      setGroup(groupRes.data);
      setMembers(membersRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = async () => {
    setShowAddModal(true);
    setSelectedClients([]);
    setSearchQuery('');
    setClients([]);  // Start with empty list
  };

  const searchClients = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      // If no search term, get first 100 clients
      try {
        const response = await api.get('/clients', { params: { limit: 100 } });
        const memberIds = members.map(m => m.client_id);
        const availableClients = response.data.clients.filter(c => !memberIds.includes(c.id));
        setClients(availableClients);
      } catch (error) {
      }
      return;
    }
    
    try {
      const response = await api.get('/clients', { params: { search: searchTerm, limit: 100 } });
      const memberIds = members.map(m => m.client_id);
      const availableClients = response.data.clients.filter(c => !memberIds.includes(c.id));
      setClients(availableClients);
    } catch (error) {
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (showAddModal) {
        searchClients(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, showAddModal, members]);

  const handleAddMembers = async () => {
    if (selectedClients.length === 0) return;
    
    setAdding(true);
    try {
      await api.post(`/wa/groups/${groupId}/members`, { client_ids: selectedClients });
      setShowAddModal(false);
      fetchGroupData();
    } catch (error) {
      alert('Error adding members');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this client from the group?')) return;
    
    try {
      await api.delete(`/wa/groups/${groupId}/members`, { 
        data: { member_ids: [memberId] } 
      });
      fetchGroupData();
    } catch (error) {
      alert('Error removing member');
    }
  };

  const toggleClient = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter(id => id !== clientId));
    } else {
      setSelectedClients([...selectedClients, clientId]);
    }
  };

  // Bulk import functions
  const openBulkModal = () => {
    setShowBulkModal(true);
    setBulkPhoneText('');
    setBulkMatches([]);
    setBulkNotFound([]);
  };

  const processBulkPhones = async () => {
    if (!bulkPhoneText.trim()) return;
    
    setBulkProcessing(true);
    
    try {
      // Parse phone numbers from text (supports various formats)
      const lines = bulkPhoneText
        .split(/[\n,;]+/)
        .map(line => line.trim())
        .filter(line => line.length > 0);
      
      // Send to backend for fast matching (single DB query instead of fetching all clients)
      const response = await api.post('/clients/bulk-match', { phones: lines });
      const { matches: serverMatches, not_found: serverNotFound } = response.data;
      
      // Get current member IDs to exclude already-in-group
      const memberIds = members.map(m => m.client_id);
      
      const matches = [];
      const notFound = [];
      
      for (const client of serverMatches) {
        if (memberIds.includes(client.id)) {
          notFound.push({ phone: client.input_phone, reason: 'Already in group', client });
        } else if (!matches.find(m => m.id === client.id)) {
          matches.push(client);
        }
      }
      
      for (const phone of serverNotFound) {
        notFound.push({ phone, reason: 'Not found in system' });
      }
      
      setBulkMatches(matches);
      setBulkNotFound(notFound);
    } catch (error) {
      alert('Error processing phone numbers');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkAdd = async () => {
    if (bulkMatches.length === 0 && (!includeUnmatched || bulkNotFound.length === 0)) return;
    
    setBulkAdding(true);
    try {
      const clientIds = bulkMatches.map(c => c.id);
      const unmatchedPhones = includeUnmatched ? bulkNotFound.map(n => n.phone) : [];
      await api.post(`/wa/groups/${groupId}/members`, { 
        client_ids: clientIds,
        unmatched_phones: unmatchedPhones
      });
      setShowBulkModal(false);
      setIncludeUnmatched(false);
      fetchGroupData();
      const total = clientIds.length + unmatchedPhones.length;
      alert(`Successfully added ${total} members to the group`);
    } catch (error) {
      alert('Error adding members');
    } finally {
      setBulkAdding(false);
    }
  };

  // Normalize phone number for comparison (used by bulk import)
  const normalizePhone = (phone) => {
    if (!phone) return '';
    let normalized = phone.replace(/[^\d+]/g, '');
    if (normalized.startsWith('+')) {
      normalized = normalized.substring(1);
    }
    if (normalized.startsWith('0')) {
      normalized = '60' + normalized.substring(1);
    }
    return normalized;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/whatsapp/groups')}
        >
          <ArrowLeft size={16} className="mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{group?.name}</h1>
          <p className="text-gray-500">{group?.description || 'No description'}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={openBulkModal}
            className="border-purple-200 text-purple-600 hover:bg-purple-50"
          >
            <FileText size={16} className="mr-2" />
            Bulk Import
          </Button>
          <Button 
            onClick={openAddModal}
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
          >
            <UserPlus size={16} className="mr-2" />
            Add Members
          </Button>
        </div>
      </div>

      {/* Members Table */}
      <Card className="bg-white border border-gray-100">
        <div className="p-4 border-b border-gray-100">
          <p className="text-sm text-gray-500">
            <span className="font-medium text-gray-900">{members.length}</span> members in this group
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Added</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.length > 0 ? members.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 font-medium text-gray-900">{member.client_name}</td>
                  <td className="px-4 py-4 text-gray-600">{member.client_phone}</td>
                  <td className="px-4 py-4 text-gray-500">
                    {new Date(member.added_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4">
                    <button 
                      onClick={() => handleRemoveMember(member.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove from group"
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" className="px-4 py-12 text-center text-gray-500">
                    No members in this group yet. Add some clients to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Members Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Members to Group</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search clients by name or phone..."
                className="pl-10"
              />
            </div>
            
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {clients.length > 0 ? clients.map((client) => (
                <div 
                  key={client.id}
                  onClick={() => toggleClient(client.id)}
                  className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 border-b last:border-0 ${
                    selectedClients.includes(client.id) ? 'bg-[#4F46E5]/5' : ''
                  }`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                    selectedClients.includes(client.id) 
                      ? 'bg-[#4F46E5] border-[#4F46E5]' 
                      : 'border-gray-300'
                  }`}>
                    {selectedClients.includes(client.id) && (
                      <Check size={14} className="text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{client.name}</p>
                    <p className="text-sm text-gray-500">{client.phone}</p>
                  </div>
                </div>
              )) : (
                <p className="p-4 text-center text-gray-500">
                  {searchQuery ? 'No clients found' : 'No clients available to add'}
                </p>
              )}
            </div>
            
            {selectedClients.length > 0 && (
              <p className="text-sm text-gray-600">
                {selectedClients.length} client(s) selected
              </p>
            )}
            
            <div className="flex justify-end gap-3 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddMembers}
                disabled={selectedClients.length === 0 || adding}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              >
                {adding ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : null}
                Add {selectedClients.length > 0 ? `${selectedClients.length} Members` : 'Members'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Modal */}
      <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Import Members</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Paste phone numbers (one per line, or comma/semicolon separated)
              </label>
              <Textarea
                value={bulkPhoneText}
                onChange={(e) => setBulkPhoneText(e.target.value)}
                placeholder="0163383029&#10;60123456789&#10;+60147894561&#10;&#10;Or: 0163383029, 60123456789, +60147894561"
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Supports formats: 0163383029, 60163383029, +60163383029
              </p>
            </div>
            
            <div className="flex justify-center">
              <Button
                onClick={processBulkPhones}
                disabled={!bulkPhoneText.trim() || bulkProcessing}
                variant="outline"
              >
                {bulkProcessing ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <Search size={16} className="mr-2" />
                )}
                Find Matching Clients
              </Button>
            </div>
            
            {/* Results */}
            {(bulkMatches.length > 0 || bulkNotFound.length > 0) && (
              <div className="space-y-4">
                {/* Matches */}
                {bulkMatches.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle size={16} className="text-green-600" />
                      <span className="text-sm font-medium text-green-700">
                        Found {bulkMatches.length} clients
                      </span>
                    </div>
                    <div className="max-h-40 overflow-y-auto border rounded-lg divide-y">
                      {bulkMatches.map((client) => (
                        <div key={client.id} className="flex items-center gap-3 p-2 bg-green-50">
                          <Check size={14} className="text-green-600" />
                          <span className="text-sm font-medium">{client.name}</span>
                          <span className="text-sm text-gray-500">{client.phone}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Not Found */}
                {bulkNotFound.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle size={16} className="text-orange-600" />
                      <span className="text-sm font-medium text-orange-700">
                        {bulkNotFound.length} numbers not matched
                      </span>
                    </div>
                    <div className="max-h-32 overflow-y-auto border rounded-lg divide-y">
                      {bulkNotFound.map((item) => (
                        <div key={item.phone} className="flex items-center gap-3 p-2 bg-orange-50">
                          <X size={14} className="text-orange-600" />
                          <span className="text-sm font-mono">{item.phone}</span>
                          <span className="text-xs text-orange-600">- {item.reason}</span>
                          {item.client && (
                            <span className="text-xs text-gray-500">({item.client.name})</span>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Option to include unmatched */}
                    <label className="flex items-center gap-2 mt-2 p-2 bg-amber-50 rounded-lg border border-amber-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeUnmatched}
                        onChange={(e) => setIncludeUnmatched(e.target.checked)}
                        className="rounded text-[#4F46E5]"
                        data-testid="include-unmatched-checkbox"
                      />
                      <div>
                        <span className="text-sm font-medium text-amber-800">Add unmatched phones anyway</span>
                        <p className="text-xs text-amber-600">These will be added as phone-only contacts (not in CRM client list)</p>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowBulkModal(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleBulkAdd}
                disabled={(bulkMatches.length === 0 && (!includeUnmatched || bulkNotFound.length === 0)) || bulkAdding}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              >
                {bulkAdding ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <UserPlus size={16} className="mr-2" />
                )}
                Add {bulkMatches.length + (includeUnmatched ? bulkNotFound.length : 0)} Members
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WAGroupsPage;
